import { useState, useEffect, forwardRef, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { TextField, IconButton } from 'ui-components-web'
import ErrorFieldForm from 'components/UI/ErrorFieldForm'
import { Autocomplete } from '@material-ui/lab'
import CircularProgress from '@material-ui/core/CircularProgress'
import { ThemeProvider } from '@material-ui/core/styles'
import { Search, ArrowDropdown } from 'components/UI/svgIcons'
import overrideTheme from 'theming/overrides/form/search'
import useStyles from './styles'

const FormSearch = ({
  label,
  options,
  loading = false,
  required = false,
  onSearch,
  onSearchParams = {},
  onSelect,
  getOptionLabel,
  onChange = () => {},
  clearable,
  onScrollToBottom = () => {},
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const isSearchEnabled = searchValue.length > 2
  const showErrorMessage = searchValue.length > 0 && searchValue.length < 3
  const classes = useStyles({ isSearchEnabled })
  const useAutocompleteInputClear = (watch, checkClear) => {
    const elmRef = useRef(null)
    useMemo(() => {
      if (!elmRef || !elmRef.current) return

      if (!checkClear || typeof checkClear !== 'function') return

      const button = elmRef.current.querySelector('button')
      if (checkClear(watch) && button) {
        button.click()
      }
    }, [watch])

    return elmRef
  }
  // Open options block on loading end
  useEffect(() => {
    if (!loading && searchValue.length) {
      setIsOpen(true)
    }
  }, [options])
  const handleSearch = () => {
    setIsOpen(false)
    onSearch(searchValue, onSearchParams)
  }
  const handleBlur = () => {
    setSearchValue('')
    setIsOpen(false)
  }
  const handleChange = (_, value) => {
    onChange(_, value)
    if (value) {
      setSearchValue(getOptionLabel(value))
      onSelect(value)
    }
  }
  const handleArrowClick = () => {
    setIsOpen(false)
  }
  const elmRef = useAutocompleteInputClear('', (v) => !v || !v.id)
  const clearableProps = clearable
    ? {
        inputValue: searchValue,
      }
    : null

  const handleListScroll = (e) => {
    if (onScrollToBottom) {
      const isBottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 30
      if (isBottom) {
        onScrollToBottom(searchValue)
      }
    }
  }

  return (
    <ThemeProvider theme={overrideTheme}>
      <div className={classes.searchContainer}>
        <Autocomplete
          open={isOpen}
          loading={loading}
          options={options}
          classes={{
            popupIndicator: isSearchEnabled ? '' : classes.disabledPopupIndicator,
          }}
          popupIcon={
            <div>
              {isOpen ? (
                <IconButton onClick={handleArrowClick} component="span">
                  <ArrowDropdown className={classes.arrowIcon} />
                </IconButton>
              ) : (
                <IconButton onClick={handleSearch} disabled={!isSearchEnabled} component="span">
                  <Search className={classes.searchIcon} />
                </IconButton>
              )}
            </div>
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              value={params.inputProps.value}
              required={required}
              onChange={(e) => {
                setSearchValue(e.target.value)
                if (options.length) setIsOpen(true)
              }}
              onBlur={handleBlur}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.value && isSearchEnabled) {
                  handleSearch()
                }
                if (e.key === 'Enter' && e.target.value.length < 3) {
                  setIsOpen(false)
                }
              }}
            />
          )}
          clearOnBlur
          blurOnSelect
          onChange={handleChange}
          ref={elmRef}
          getOptionLabel={getOptionLabel}
          getOptionSelected={(option, value) => option.id === value.id}
          {...props}
          {...clearableProps}
          ListboxProps={{
            onScroll: handleListScroll,
          }}
        />
        {showErrorMessage && <ErrorFieldForm>Search must be at least 3 characters</ErrorFieldForm>}
      </div>
    </ThemeProvider>
  )
}
FormSearch.propTypes = {
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  required: PropTypes.bool,
  onSearch: PropTypes.func.isRequired,
  onSearchParams: PropTypes.object,
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  getOptionLabel: PropTypes.func,
  onScrollToBottom: PropTypes.func,
}
FormSearch.defaultProps = {
  label: '',
  required: false,
  onSearchParams: {},
  onChange: () => {},
  onSelect: () => {},
  getOptionLabel: (option) => option.label,
}
export default FormSearch
